import React, { useCallback, useContext, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import {
  Box,
  Button,
  DND_DRAGGABLE_PROPS,
  Icon,
  type IconName,
  InputGroup,
  InputGroupInput,
  Separator,
  sortableHandleProps,
  sortableStyle,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsTouchDevice,
} from "@salestrak/ui";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import {
  FieldsSearchInputContext,
  OptionsPanelHeaderRung,
} from "./options-panel-slots";

// ---------------------------------------------------------------------------
// Display-fields row grammar — shared by every view's Fields page
// ---------------------------------------------------------------------------
//
// The Fields page of a view's display options is the same panel whatever the
// view type: a search rung in the header block, then sections of icon + label
// + switch rows ("Shown" reorderable, hidden groups plain). These are those
// pieces; each view supplies its own items and its own persistence.
// ---------------------------------------------------------------------------

/** One flattened panel row — a property or a custom field. */
export interface DisplayItem {
  /** The view's order key: property key or custom-field column id. */
  key: string;
  label: string;
  icon: IconName;
  /** Custom fields carry their type color; properties stay muted. */
  iconColor?: React.ComponentProps<typeof Icon>["color"];
  kind: "property" | "field";
  shown: boolean;
  /** The custom field a `field` row stands for — what its hover-revealed
   *  "Edit field" action opens in the Custom Field Manager. */
  field?: CustomFieldDef;
}

/**
 * The search rung: part of the panel's header block — the framed well (no
 * leading icon, on purpose) sits on the 24px content line under the title
 * row (whose own bottom padding supplies the gap), and the hairline below it
 * is where the header ends and the list begins — the panel skips its
 * separator on the Fields page for this. It rides `OptionsPanelHeaderRung`
 * so it stays above the scrolling list; the caret ref comes from the shell
 * the same way (the input's state lives here, two owners below it).
 */
export function FieldsSearch({
  value,
  onValueChange,
  placeholder = "Search Fields",
  children,
  hairline = true,
}: {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  /** Rides the rung under the well, above its hairline — the add-field
   *  picker's tab strip. With it, the well keeps only a 4px gap below: the
   *  strip owns the rest of the rung, flush on the hairline. */
  children?: React.ReactNode;
  hairline?: boolean;
}) {
  const inputRef = useContext(FieldsSearchInputContext);
  return (
    <OptionsPanelHeaderRung>
      <Box paddingX="lg" paddingBottom={children ? "xs" : "base"}>
        <InputGroup>
          <InputGroupInput
            ref={inputRef ?? undefined}
            type="search"
            value={value}
            onValueChange={onValueChange}
            placeholder={placeholder}
          />
        </InputGroup>
      </Box>
      {children}
      {hairline ? <Separator /> : null}
    </OptionsPanelHeaderRung>
  );
}

/** Group header — the quiet faint rung, count flush right (item groups
 *  only). */
export function SectionHeader({
  label,
  count,
}: {
  label: string;
  count?: number | undefined;
}) {
  return (
    <Box
      direction="row"
      align="center"
      justify="between"
      paddingX="sm"
      paddingTop="2xs"
      paddingBottom="xs"
    >
      <Text size="xs" weight="medium" variant="faint">
        {label}
      </Text>
      {count !== undefined ? (
        <Text size="xs" variant="faint">
          {count}
        </Text>
      ) : null}
    </Box>
  );
}

/**
 * Custom-field rows offer the field's editor on hover, seated before the
 * switch — the same "Edit field" a column header's menu offers, opening the
 * Custom Field Manager on that field. `custom_fields.manage` only; without it
 * the slot stays empty rather than showing a control that cannot act.
 */
function EditFieldAction({ field }: { field: CustomFieldDef }) {
  const canManage = usePermission("custom_fields.manage");
  const { openDialog } = useCustomFieldsDialogActions();
  if (!canManage) return null;
  return (
    <Box hoverReveal="row" flex="none">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              size="icon-xs"
              shape="square"
              variant="ghost"
              aria-label="Edit field"
              onClick={() => openDialog({ editField: field })}
            />
          }
        >
          <Icon name="pencil" size="sm" color="muted" />
        </TooltipTrigger>
        <TooltipPopup>Edit field</TooltipPopup>
      </Tooltip>
    </Box>
  );
}

/** Shared row shell: [16px leading slot] label … [hover action] switch.
 *  Press toggles (the Customize view row grammar: 32px floor, 8px inset). */
export function RowShell({
  item,
  leading,
  onToggle,
  pressDisabled,
}: {
  item: DisplayItem;
  leading: React.ReactNode;
  onToggle: (item: DisplayItem, shown: boolean) => void;
  /** Suppresses the row press mid-drag so the drop's phantom click doesn't
   *  also toggle. */
  pressDisabled?: boolean;
}) {
  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      hoverBackground
      cursor="pointer"
      role="listitem"
      aria-label={`Toggle ${item.label}`}
      group="row"
      onClick={pressDisabled ? undefined : () => onToggle(item, !item.shown)}
    >
      <Box width={16} align="center" justify="center" flex="none">
        {leading}
      </Box>
      <Box direction="column" shrink fill="flex" minWidth={0}>
        <Text size="sm" truncate={1}>
          {item.label}
        </Text>
      </Box>
      {/* Stop pointer-down so neither control arms a row drag, and the click
          so the Switch's own change (or the edit action) doesn't double with
          the row's toggle. */}
      <Box
        direction="row"
        align="center"
        gap="xs"
        flex="none"
        onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {item.field ? <EditFieldAction field={item.field} /> : null}
        <Switch
          size="sm"
          checked={item.shown}
          onCheckedChange={(checked) => onToggle(item, !!checked)}
        />
      </Box>
    </Box>
  );
}

function itemIcon(item: DisplayItem) {
  return <Icon name={item.icon} size="md" color={item.iconColor ?? "muted"} />;
}

/** Hidden-group row — plain icon, no drag (lives outside any DndContext). */
export function ItemRow({
  item,
  onToggle,
}: {
  item: DisplayItem;
  onToggle: (item: DisplayItem, shown: boolean) => void;
}) {
  return <RowShell item={item} leading={itemIcon(item)} onToggle={onToggle} />;
}

/**
 * Shown-group row: sortable. Hovering swaps the type icon for the drag
 * handle in the SAME 16px slot, and dragging reorders the view's shown
 * items. Touch keeps the icon (no hover to swap it on) and drags the row
 * after a hold — the list's touch sensor — while a swipe scrolls the panel.
 */
export function SortableItemRow({
  item,
  sortable,
  onToggle,
}: {
  item: DisplayItem;
  /** False while a search filters the list (indices wouldn't match). */
  sortable: boolean;
  onToggle: (item: DisplayItem, shown: boolean) => void;
}) {
  const isTouch = useIsTouchDevice();
  const [hovered, setHovered] = useState(false);
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: item.key, disabled: !sortable });

  const nodeRef = useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );

  const showGrip = sortable && !isTouch && (hovered || isDragging);

  return (
    <Box
      ref={nodeRef}
      // The row is the handle AND the panel's scrollable content, so it
      // keeps panning; the hold-activated touch sensor prevents the scroll
      // once a hold completes.
      touchAction="manipulation"
      {...DND_DRAGGABLE_PROPS}
      cursor={isDragging ? "grabbing" : undefined}
      style={sortableStyle(transform, {
        transition,
        isSorting,
        isDragging,
        axis: "y",
        dragOpacity: 0.3,
      })}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      {...sortableHandleProps(attributes, listeners)}
    >
      <RowShell
        item={item}
        onToggle={onToggle}
        // isDragging is still true when the browser fires the drop's click
        // (the re-render lands after) — the same disambiguation KanbanCard
        // and the column-settings rows use.
        pressDisabled={isDragging}
        leading={
          showGrip ? (
            <Icon name="grip-vertical" size="sm" color="muted" />
          ) : (
            itemIcon(item)
          )
        }
      />
    </Box>
  );
}

/** A pinned row — every view leads with its title field; it can't be hidden
 *  or reordered (disabled switch, no drag handle). */
export function PinnedRow({ label, icon }: { label: string; icon: IconName }) {
  return (
    <Box direction="row" align="center" gap="sm" minHeight={32} paddingX="sm">
      <Box width={16} align="center" justify="center" flex="none">
        <Icon name={icon} size="md" color="muted" />
      </Box>
      <Box direction="column" shrink fill="flex" minWidth={0}>
        <Text size="sm" variant="muted" truncate={1}>
          {label}
        </Text>
      </Box>
      <Switch size="sm" checked disabled />
    </Box>
  );
}
